/**
 * Live-events wire contract — the JSON pushed over the `/api/events/ws` WebSocket
 * from the `EventsHub` Durable Object to the web app (ARCHITECTURE.md §5, §7).
 *
 * Queue-backed work (backtests, snapshots, notifications) finishes asynchronously;
 * these are the fire-and-forget "something you were waiting on changed" pings the
 * client uses to refresh, so the operator never has to reload. They are a
 * best-effort UX signal — NOT an ordering/delivery guarantee and never a
 * substitute for the authoritative GET the client already has. The envelope is
 * versioned (`v`) so a future breaking shape can coexist on the same socket.
 *
 * FROZEN: the web client codes against exactly these schemas. Add a new
 * `LiveEvent` variant rather than mutating an existing one.
 */
import { z } from "zod";

/** One live event: a discriminated union keyed on `kind` (the thing that changed). */
export const LiveEventSchema = z.discriminatedUnion("kind", [
  /** A backtest run advanced: `running` (a chunk committed, `cursor` = memo length), `done`, or `error`. */
  z.object({
    kind: z.literal("backtest"),
    backtestId: z.string(),
    /** Owning strategy — lets the client invalidate one strategy's queries instead of the whole family. */
    strategyId: z.string(),
    status: z.enum(["running", "done", "error"]),
    cursor: z.number().optional(),
  }),
  /** A portfolio snapshot for one account settled (`done`) or dead-lettered (`failed`). */
  z.object({
    kind: z.literal("snapshot"),
    accountSeq: z.string(),
    status: z.enum(["done", "failed"]),
  }),
  /** A notification row was inserted (the client refetches its feed by id). */
  z.object({
    kind: z.literal("notification"),
    notificationId: z.string(),
  }),
]);
export type LiveEvent = z.infer<typeof LiveEventSchema>;

/** The wire envelope every socket frame carries: version, server timestamp, event. */
export const LiveEventEnvelopeSchema = z.object({
  v: z.literal(1),
  ts: z.number(),
  event: LiveEventSchema,
});
export type LiveEventEnvelope = z.infer<typeof LiveEventEnvelopeSchema>;
